import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { useParams } from 'react-router-dom'
import { ModalSize, showAlert } from '_metronic/helpers'
import { Button, ListLoading, useModal } from 'app/components'
import { getAtendimentoById } from '../../atendimento/core/_requests'
import { idEmpresaSncrDe } from '../../sncr/core/_models'
import { PrescricaoView } from './core/_model'
import { ItemCatalogo } from './core/montagem/itens'
import { isEmissaoDisponivel } from './core/receituario'
import { CabecalhoPaciente } from './components/cabecalho/CabecalhoPaciente'
import { BuscaPrescricao } from './components/busca/BuscaPrescricao'
import { ID_BOTAO_REVISAR } from './components/montagem/ids'
import { PrescricaoAtual } from './components/montagem/PrescricaoAtual'
import { useCadastroItem } from './components/montagem/useCadastroItem'
import { UltimasPrescricoes } from './components/historico/UltimasPrescricoes'
import { RevisaoDrawer } from './components/revisao/RevisaoDrawer'
import { AssinaturaModal } from './components/assinatura/AssinaturaModal'
import { DadosSusModal } from './components/sus/DadosSusModal'
import { ResultadoPrescricao } from './components/resultado/ResultadoPrescricao'
import { DocumentoModal, DocumentoModalRef } from './components/modal/DocumentoModal'
import { useRotulos } from './components/comum/useRotulos'
import { PreferenciasSus, precisaDeApac, precisaDeLme } from './core/documentosSus'
import { CanalEnvio, PreferenciasEnvio, formatarTelefone } from './core/montagem/envio'
import { useAlergiasPaciente } from './hooks/useAlergiasPaciente'
import { useAssinaturaDocumentos } from './hooks/useAssinaturaDocumentos'
import { useEnvioPaciente } from './hooks/useEnvioPaciente'
import { useHistoricoPrescricoes } from './hooks/useHistoricoPrescricoes'
import { PendenciaItem, useMontagemPrescricao } from './hooks/useMontagemPrescricao'
import styles from './Prescricao.module.scss'

/**
 * Prescrições que o modal SUS trata, fixadas na abertura. Recarregar depois de uma falha parcial
 * não muda a lista: o formulário não reinicia e a nova tentativa gera só o que falta (inclusive
 * só o termo), já que `gerarLme`/`gerarApac` conferem cada prescrição no backend.
 */
type SusAberto = { lme: PrescricaoView[]; apac: PrescricaoView[] } | null
const NENHUMA: PrescricaoView[] = []

const ENVIO_INICIAL: PreferenciasEnvio = {
  whatsapp: true,
  sms: false,
  email: true,
  telefone: '',
  enderecoEmail: '',
}

/**
 * Prescrição: Montar → Revisar → Assinar → Resultado. A página só orquestra: a montagem vive em
 * `useMontagemPrescricao`, a gravação e a assinatura em `useAssinaturaDocumentos`, e cada etapa
 * é um componente próprio. Nada é gravado no backend antes do certificado estar pronto.
 */
export const PrescricaoCreate = () => {
  const { t } = useRotulos()
  const { showModal } = useModal()
  const abrirCadastro = useCadastroItem()
  const documentoModalRef = useRef<DocumentoModalRef>(null)
  const { atendimentoId } = useParams<{ atendimentoId: string }>()
  const idAtendimento = Number(atendimentoId)

  const {
    data: atendimento,
    isLoading,
    isError,
    refetch,
  } = useQuery(['prescricao-atendimento', idAtendimento], () => getAtendimentoById(idAtendimento), {
    enabled: !!idAtendimento,
    refetchOnWindowFocus: false,
  })
  const atendeSUS = !!(atendimento?.empresa?.sus || atendimento?.unidade?.sus)
  const idEmpresaSncr = idEmpresaSncrDe(atendimento)
  const pacienteId = atendimento?.paciente?.id ? Number(atendimento.paciente.id) : undefined
  const nomePaciente = atendimento?.paciente?.pessoa?.nome ?? atendimento?.paciente?.nome

  const alergias = useAlergiasPaciente(pacienteId)
  const montagem = useMontagemPrescricao()
  const historico = useHistoricoPrescricoes(pacienteId)
  const assinatura = useAssinaturaDocumentos(idAtendimento)
  const envio = useEnvioPaciente()

  const [fase, setFase] = useState<'montagem' | 'resultado'>('montagem')
  const [revisaoAberta, setRevisaoAberta] = useState(false)
  // Saindo da revisão por uma pendência, o foco vai ao campo, não de volta ao botão que abriu.
  const [devolverFocoRevisao, setDevolverFocoRevisao] = useState(true)
  // Depois da primeira revisão, o que falta nos cards vira erro (vermelho, ligado ao campo).
  const [revisaoTentada, setRevisaoTentada] = useState(false)
  const [assinando, setAssinando] = useState(false)
  const [susAberto, setSusAberto] = useState<SusAberto>(null)
  const [preferenciasSus, setPreferenciasSus] = useState<PreferenciasSus>({
    lme: 'apos_assinar',
    apac: 'apos_assinar',
  })
  const [preferenciasEnvio, setPreferenciasEnvio] = useState<PreferenciasEnvio>(ENVIO_INICIAL)

  // Telefone e e-mail do cadastro, quando o atendimento chega (sem apagar o que já foi editado).
  const pessoa = atendimento?.paciente?.pessoa
  const telefoneCadastro = formatarTelefone(pessoa?.telefoneCelular || pessoa?.telefone)
  const emailCadastro = pessoa?.usuario?.email ?? ''
  useEffect(() => {
    setPreferenciasEnvio((atual) => ({
      ...atual,
      telefone: atual.telefone || telefoneCadastro,
      enderecoEmail: atual.enderecoEmail || emailCadastro,
      email: atual.enderecoEmail || emailCadastro ? atual.email : false,
    }))
  }, [telefoneCadastro, emailCadastro])

  const assinados = useMemo(
    () => assinatura.documentos.filter((doc) => doc.estado === 'assinado'),
    [assinatura.documentos]
  )
  const idsAssinados = assinados.map((doc) => doc.prescricao.id)

  /** Abre o modal SUS com as prescrições assinadas que ainda pedem LME e/ou APAC. */
  const abrirSus = (lme: boolean, apac: boolean) => {
    const prescricoes = assinatura.documentos
      .filter((doc) => doc.estado === 'assinado')
      .map((doc) => doc.prescricao)
    const aberto = {
      lme: lme ? prescricoes.filter(precisaDeLme) : NENHUMA,
      apac: apac ? prescricoes.filter(precisaDeApac) : NENHUMA,
    }
    if (aberto.lme.length > 0 || aberto.apac.length > 0) setSusAberto(aberto)
  }

  const abrirRevisao = () => {
    setRevisaoTentada(true)
    setDevolverFocoRevisao(true)
    setRevisaoAberta(true)
  }

  const irParaItem = (pendencia: PendenciaItem) => {
    setDevolverFocoRevisao(false)
    setRevisaoAberta(false)
    montagem.irParaCampo(pendencia.chave, pendencia.idCampo)
  }

  /** Rascunho que o backend não deixou apagar fica sem dono na tela: o médico precisa saber. */
  const avisarRascunhosOrfaos = (quantidade: number) => {
    if (quantidade > 0) {
      showAlert({
        title: t('prescricao.rascunhos_orfaos.titulo'),
        text: t('prescricao.rascunhos_orfaos.texto', { quantidade }),
        icon: 'warning',
      })
    }
  }

  /** Talidomida: a plataforma não emite o documento dela; o backend também recusa ao salvar. */
  const adicionarItem = (catalogo: ItemCatalogo) => {
    if (!isEmissaoDisponivel(catalogo.receituario)) {
      showAlert({
        title: t('prescricao.talidomida.titulo'),
        text: t('prescricao.talidomida.texto', { nome: catalogo.nome }),
        icon: 'warning',
      })
      return
    }
    montagem.adicionar(catalogo)
  }

  const cancelarAssinatura = async () => {
    avisarRascunhosOrfaos(await assinatura.descartarPendentes())
    setAssinando(false)
    document.getElementById(ID_BOTAO_REVISAR)?.focus()
  }

  const concluirAssinatura = () => {
    setAssinando(false)
    setFase('resultado')
    historico.atualizar()
    const ids = assinatura.documentos
      .filter((doc) => doc.estado === 'assinado')
      .map((doc) => doc.prescricao.id)
    envio.enviar(ids, preferenciasEnvio)
    if (atendeSUS) {
      abrirSus(preferenciasSus.lme === 'apos_assinar', preferenciasSus.apac === 'apos_assinar')
    }
  }

  const novaPrescricao = async () => {
    avisarRascunhosOrfaos(await assinatura.descartarPendentes())
    assinatura.reiniciar()
    montagem.limpar()
    envio.limpar()
    setRevisaoTentada(false)
    setFase('montagem')
  }

  const abrirAtestados = () =>
    showModal({
      title: t('prescricao.paciente.atestados'),
      content: <DocumentoModal ref={documentoModalRef} onConfirm={() => historico.atualizar()} />,
      fetchers: { atendimento: () => getAtendimentoById(idAtendimento) },
      size: ModalSize.Medium,
      onClose: () => documentoModalRef.current?.onClose(),
    })

  if (isLoading) return <ListLoading />

  // Sem o atendimento não há empresa, SUS nem SNCR: assinar assim gravaria a receita errada.
  if (isError || !atendimento) {
    return (
      <div className={`card card-bordered ${styles.tema}`} role='alert'>
        <div className='card-body d-flex flex-wrap align-items-center gap-4'>
          <span className='flex-grow-1 fw-semibold'>{t('prescricao.erro_atendimento')}</span>
          <Button className='btn-primary' onClick={() => refetch()}>
            {t('prescricao.tentar_de_novo')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.tema}>
      <CabecalhoPaciente
        atendimento={atendimento}
        alergias={alergias}
        onAtestados={abrirAtestados}
      />

      {fase === 'resultado' ? (
        <ResultadoPrescricao
          documentos={assinatura.documentos}
          nomePaciente={nomePaciente}
          atendeSUS={atendeSUS}
          idEmpresaSncr={idEmpresaSncr}
          envio={{ preferencias: envio.preferencias, estados: envio.estados }}
          onReenviar={(canal: CanalEnvio) => envio.enviar(idsAssinados, preferenciasEnvio, [canal])}
          onAssinarDeNovo={assinatura.tentarDeNovo}
          onGerarSus={(documento) => abrirSus(documento === 'lme', documento === 'apac')}
          onNovaPrescricao={novaPrescricao}
        />
      ) : (
        <>
          <BuscaPrescricao
            atendeSUS={atendeSUS}
            alergias={alergias}
            jaAdicionado={montagem.jaAdicionado}
            onAdicionar={adicionarItem}
            onCadastrar={(categoria) => abrirCadastro(categoria, adicionarItem)}
          />
          <PrescricaoAtual
            montagem={montagem}
            atendeSUS={atendeSUS}
            mostrarErros={revisaoTentada}
            onRevisar={abrirRevisao}
            onEditarCadastro={(item) =>
              abrirCadastro(
                item.categoria,
                (catalogo) => montagem.atualizarCatalogo(item.chave, catalogo),
                item.catalogo
              )
            }
          />
          <UltimasPrescricoes
            prescricoes={historico.prescricoes}
            carregando={historico.carregando}
            nomePaciente={nomePaciente}
            onAdicionar={montagem.adicionarVarios}
          />
        </>
      )}

      <RevisaoDrawer
        aberto={revisaoAberta}
        devolverFoco={devolverFocoRevisao}
        itens={montagem.itens}
        documentos={montagem.documentos}
        pendenciasItens={montagem.pendencias}
        atendeSUS={atendeSUS}
        idEmpresaSncr={idEmpresaSncr}
        envio={preferenciasEnvio}
        onEnvioChange={setPreferenciasEnvio}
        sus={preferenciasSus}
        onSusChange={setPreferenciasSus}
        onIrParaItem={irParaItem}
        onFechar={() => setRevisaoAberta(false)}
        onAssinar={() => {
          setRevisaoAberta(false)
          setAssinando(true)
        }}
      />

      <AssinaturaModal
        aberto={assinando}
        itens={montagem.itens}
        quantidadeDocumentos={montagem.documentos.length}
        assinatura={assinatura}
        onCancelar={cancelarAssinatura}
        onConcluir={concluirAssinatura}
      />

      <DadosSusModal
        aberto={!!susAberto}
        prescricoesLme={susAberto?.lme ?? NENHUMA}
        prescricoesApac={susAberto?.apac ?? NENHUMA}
        tipo={assinatura.tipoCertificado}
        atendimento={atendimento}
        onRecarregar={() => assinatura.recarregar(idsAssinados)}
        onGerado={() => setSusAberto(null)}
        onDepois={() => setSusAberto(null)}
      />
    </div>
  )
}
